'use client'

import React, { FC, ReactElement } from 'react'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { EXPLORER_URL, API_URL, NETWORK } from '@/utils/constants'
import { Input } from '@/components/ui/input'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import useWallet from '@/utils/hooks/useWallet'
import Outpoint from '@/utils/outpoint'
import { ToastAction } from '@/components/ui/toast'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import {
	getInscriptionCollectionSlugs,
	inscriptionBelongsToCollection
} from '@/utils/inscription-collections'

import useSelect from '../../hooks/useSelect'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import Item from './base-item'

const CollectionDetailConsolidate: FC<{
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
	collectionFilter?: string
}> = (props): ReactElement => {
	const {
		onClose,
		args,
		tab: _tab,
		onChangeTab: _onChangeTab,
		token: _token,
		collectionFilter: collectionFilterProp
	} = props
	const { slug } = args
	const { toast } = useToast()
	const { exchangeRate } = useExchangeRate()
	const { data: walletData, isLoading, mutate } = useWallet()

	const data = React.useMemo(() => {
		if (!walletData?.inscriptions || !Array.isArray(walletData.inscriptions)) return []
		return walletData.inscriptions.map((e) => ({
			...e,
			inscription_id: e.id,
			amount: 1,
			escrow: e.escrow,
			outpoint: e.id,
			// @ts-ignore
			sats: e.outpoint.sats,
			// @ts-ignore
			utxoOutpoint: e.outpoint.outpoint
		}))
	}, [walletData])

	const [confirmed, setConfirmed] = React.useState(false)
	const collectionFilter = collectionFilterProp || 'all'
	const [to, setTo] = React.useState('')
	const handleChangeTo = React.useCallback((evt: any) => setTo(evt.target.value), [])
	const [computedServiceFee, setComputedServiceFee] = React.useState(0)
	const [computedOutputValue, setComputedOutputValue] = React.useState(0)

	const filteredData = React.useMemo(() => {
		if (!data || !Array.isArray(data)) return []
		if (collectionFilter === 'all') return data
		if (collectionFilter === 'uncategorized') {
			return data.filter((e) => getInscriptionCollectionSlugs(e).length === 0)
		}
		return data.filter((e) => inscriptionBelongsToCollection(e, collectionFilter))
	}, [data, collectionFilter])

	const sortedData = React.useMemo(() => {
		return [...filteredData].sort((a, b) => (b.sats || 0) - (a.sats || 0))
	}, [filteredData])

	// Group inscriptions by their actual UTXO outpoint
	const utxoGroups = React.useMemo(() => {
		if (!data) return new Map()
		const groups = new Map<string, string[]>()
		for (const item of data) {
			const key = item.utxoOutpoint
			if (!key) continue
			if (!groups.has(key)) groups.set(key, [])
			groups.get(key)!.push(item.outpoint)
		}
		return groups
	}, [data])

	const ids = React.useMemo(() => {
		return sortedData.map((e) => e.outpoint)
	}, [sortedData])
	const { selected, setSelected, onSelect, onSelectAll, slider, onSliderChange, allSelected } =
		useSelect(ids)

	// Select/deselect all inscriptions on the same UTXO together
	const onSelectGroup = React.useCallback(
		(id: string) => {
			const item = data?.find((e: any) => e.outpoint === id)
			if (!item?.utxoOutpoint) return onSelect(id)
			const group = utxoGroups.get(item.utxoOutpoint)
			if (!group || group.length <= 1) return onSelect(id)
			const isSelected = selected.has(id)
			const newSet = new Set(selected)
			for (const inscId of group) {
				if (isSelected) newSet.delete(inscId)
				else newSet.add(inscId)
			}
			setSelected(newSet)
		},
		[data, utxoGroups, selected, onSelect, setSelected]
	)

	// Get unique UTXO values for a selection set (dedup shared UTXOs)
	const getSelectedUtxoTotals = React.useCallback(
		(sel: Set<string>) => {
			const utxoMap = new Map<string, { value: number; txid: string; vout: number }>()
			const dataByOutpoint = new Map((data || []).map((a: any) => [a.outpoint, a]))
			// @ts-ignore
			for (let e of sel.entries()) {
				const item = dataByOutpoint.get(e[0])
				if (item) {
					const key = item.utxoOutpoint || item.outpoint
					if (!utxoMap.has(key)) {
						const { txid, vout } = Outpoint.decode(key)
						utxoMap.set(key, {
							value: item.sats || 0,
							txid,
							vout
						})
					}
				}
			}
			let totalValue = 0
			utxoMap.forEach((v) => {
				totalValue += v.value
			})
			return { totalValue, utxoCount: utxoMap.size, utxoMap }
		},
		[data]
	)

	//
	// Renders
	//

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			const sats = e.sats || 0
			const usd = ((sats / 1e8) * exchangeRate)?.toFixed(2)
			const group = e.utxoOutpoint ? utxoGroups.get(e.utxoOutpoint) : null
			const groupSize = group?.length || 1
			return (
				<Item data={e} selected={selected.has(id)} onSelect={() => onSelectGroup(id)} slug={slug}>
					<p className="text-xs text-muted-foreground whitespace-nowrap">
						{formatNumber(sats)} sats / ${formatNumber(usd)}
						{groupSize > 1 && <span className="ml-1 text-yellow-500">×{groupSize}</span>}
					</p>
				</Item>
			)
		},
		[exchangeRate, utxoGroups, selected, slug, onSelectGroup]
	)

	const handleSubmit = React.useCallback(
		async (psbt: any) => {
			const rawtx = await Wallet.signPSBTExtractTx(psbt)
			const { data: broadcastData } = await axios.post(`${API_URL}/wallet/broadcast`, {
				rawtx: rawtx
			})

			if (broadcastData.error) {
				toast({
					title: 'Failed to broadcast',
					description: broadcastData.error.message
				})
			} else {
				const { utxoCount } = getSelectedUtxoTotals(selected)
				toast({
					title: 'Consolidation successful',
					description: `Merged ${utxoCount} UTXOs into 1 output`,
					action: (
						<ToastAction
							altText="View Transaction"
							onClick={() => window.open(`${EXPLORER_URL}/tx/${broadcastData.result}`, '_blank')}
						>
							View Transaction
						</ToastAction>
					)
				})
			}

			mutate()
			onClose()
		},
		[mutate, onClose, toast, getSelectedUtxoTotals, selected]
	)

	const handleBuildPsbt = React.useCallback(
		async (deps: any[], feeRate: number) => {
			const [dSelected, dTo] = deps

			if (dSelected.size === 0 || !dTo) {
				return null
			}

			const { totalValue, utxoMap } = getSelectedUtxoTotals(dSelected)

			if (totalValue === 0) return null

			const utxos: [string, number, number][] = []
			utxoMap.forEach((v) => {
				utxos.push([v.txid, v.vout, v.value])
			})

			// Build with dummy output to calculate total overhead (network + service fees)
			// The builder adds a service fee output and change output automatically.
			// totalInput = dummyOutput + serviceFee + change + networkFees
			// We want: realOutput = totalInput - serviceFee - networkFees
			// So: realOutput = dummyOutput + change (from the dummy build)
			const dummyOutput = 546
			const from = Wallet.getTaprootAddress()
			const dummyPayload = {
				outputs: [[dTo, dummyOutput]],
				public_key: Wallet.getPublicKey(),
				from,
				utxos,
				fee_rate: feeRate,
				estimate: true
			}

			console.log(
				'[consolidate] START totalValue=' +
					totalValue +
					' utxoCount=' +
					utxos.length +
					' feeRate=' +
					feeRate +
					' to=' +
					dTo.slice(0, 20)
			)

			const t0 = performance.now()
			const { data: estimateData } = await axios.post(`${API_URL}/wallet/build`, dummyPayload)
			console.log(
				'[consolidate] dummy build took ' +
					(performance.now() - t0).toFixed(0) +
					'ms fees=' +
					estimateData.fees
			)

			// Find change sent back to our address in the dummy PSBT
			const totalToFrom = Wallet.getPSBTOutputValue(estimateData.psbt, from)
			// If dTo == from, our dummy output is also counted in totalToFrom
			const change = dTo === from ? totalToFrom - dummyOutput : totalToFrom
			const outputValue = dummyOutput + change
			const networkFees = estimateData.fees
			const serviceFee = totalValue - outputValue - networkFees
			setComputedServiceFee(serviceFee)
			setComputedOutputValue(outputValue)

			console.log(
				'[consolidate] outputValue=' +
					outputValue +
					' networkFees=' +
					networkFees +
					' serviceFee=' +
					serviceFee +
					' change=' +
					change
			)

			if (outputValue <= 0) return null

			// Build and absorb any leftover change back into the output.
			// The API may add a change output if the value encoding changes tx weight.
			let finalOutputValue = outputValue
			let buildData
			for (let attempt = 0; attempt < 2; attempt++) {
				const payload = {
					outputs: [[dTo, finalOutputValue]],
					public_key: Wallet.getPublicKey(),
					from,
					utxos,
					fee_rate: feeRate,
					purpose: 'utxo_consolidate'
				}

				const t1 = performance.now()
				const { data } = await axios.post(`${API_URL}/wallet/build`, payload)
				buildData = data
				console.log(
					'[consolidate] build attempt=' +
						attempt +
						' took ' +
						(performance.now() - t1).toFixed(0) +
						'ms fees=' +
						data.fees
				)

				// Check if there's still change going back to `from`
				const remainingChange =
					dTo === from
						? Wallet.getPSBTOutputValue(data.psbt, from) - finalOutputValue
						: Wallet.getPSBTOutputValue(data.psbt, from)

				if (remainingChange > 0) {
					console.log('[consolidate] absorbing leftover change=' + remainingChange)
					finalOutputValue += remainingChange
					continue
				}
				break
			}

			const fee = Wallet.getPSBTFee(buildData.psbt)
			setComputedOutputValue(finalOutputValue)
			const finalServiceFee = totalValue - finalOutputValue - Number(fee)
			setComputedServiceFee(finalServiceFee)

			console.log(
				'[consolidate] final outputValue=' +
					finalOutputValue +
					' fee=' +
					Number(fee) +
					' serviceFee=' +
					finalServiceFee +
					' totalTime=' +
					(performance.now() - t0).toFixed(0) +
					'ms'
			)

			return { psbt: buildData.psbt, fee }
		},
		[getSelectedUtxoTotals]
	)

	const invoiceItems = React.useMemo(() => {
		return [
			{
				title: 'Total Refund',
				valueFunc: ({ building, psbt }: any) => {
					if (building || !psbt) return '--'
					return `${formatSatoshis(computedOutputValue)} ${NETWORK} / $${formatNumber(
						((computedOutputValue / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{
				title: 'Service Fee',
				valueFunc: ({ building, psbt }: any) => {
					if (building || !psbt) return '--'
					return `${formatSatoshis(computedServiceFee)} ${NETWORK} / $${formatNumber(
						((computedServiceFee / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{ title: 'Network Fee' },
			{
				title: 'Total Fees',
				valueFunc: ({ building, fee, psbt }: any) => {
					if (building || !psbt) return '--'
					const totalFee = Number(fee) + computedServiceFee
					return `${formatSatoshis(totalFee)} ${NETWORK} / $${formatNumber(
						((totalFee / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			}
		]
	}, [exchangeRate, computedServiceFee, computedOutputValue])

	// Clear selection when filter changes
	React.useEffect(() => {
		setSelected(new Set())
	}, [collectionFilter, setSelected])

	// Memoize buildDeps so re-renders from setComputedServiceFee/setComputedOutputValue
	// don't create a new array reference and re-trigger the invoice build
	const buildDeps = React.useMemo(() => [selected, to], [selected, to])

	return (
		<>
			<div className="mx-2 my-2 p-3 rounded-lg border border-red-500/30 bg-red-500/5">
				<p className="text-xs text-destructive font-medium mb-1.5">Warning</p>
				<p className="text-[11px] text-destructive/80 leading-relaxed">
					This process will burn the selected inscriptions. Send these inscriptions out to a non
					ordinal-aware wallet to recoup all of the satoshis contained in the inscriptions.
				</p>
				<label className="flex items-center gap-2 mt-2.5 cursor-pointer select-none">
					<input
						type="checkbox"
						checked={confirmed}
						onChange={(e) => setConfirmed(e.target.checked)}
						className="size-3.5 rounded border-red-500/50 accent-red-500"
					/>
					<span className="text-[11px] text-destructive/90">
						I understand these inscriptions will be burned
					</span>
				</label>
			</div>
			<div className="flex px-2 mt-2 flex-col w-full gap-1">
				<p className="text-[10px] text-muted-foreground">Enter a Non-Ordinal Wallet Address</p>
				<Input placeholder="Burn To Address" className="h-8" value={to} onChange={handleChangeTo} />
			</div>
			{isLoading ? (
				<div className="flex items-center justify-center min-h-[120px]">
					<Loading size={16} />
				</div>
			) : sortedData?.length === 0 ? (
				<div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
					No inscriptions found
				</div>
			) : (
				<>
					<div className="flex justify-between items-center px-2 py-3 gap-2">
						<div className="flex gap-2 items-center">
							<SelectButton selected={allSelected} onSelect={onSelectAll} />
							<p className="text-xs text-muted-foreground select-none whitespace-nowrap">
								Quantity ({selected.size > 0 ? `${selected.size}/` : ''}
								{sortedData?.length} lots)
							</p>
						</div>
						<Slider
							defaultValue={[slider]}
							value={[slider]}
							max={sortedData?.length}
							step={1}
							onValueChange={onSliderChange}
						/>
					</div>
					{sortedData?.map(renderOutpoint)}
				</>
			)}
			<Invoice
				open={selected.size > 1 && confirmed && !!to}
				onBuild={handleBuildPsbt}
				buildDeps={buildDeps}
				onSubmit={handleSubmit}
				action={({ building, fee }) => {
					if (building || !fee) return 'Calculating...'
					const { utxoCount } = getSelectedUtxoTotals(selected)
					return `Burn ${utxoCount} UTXOs`
				}}
				items={invoiceItems}
			/>
		</>
	)
}

export default CollectionDetailConsolidate
